/**
 * React App Entry Point
 */

import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext';
import { ActivityProvider } from './contexts/ActivityContext';
import { HotkeyProvider } from './lib/hotkeys';
import { sendClientLog } from './lib/clientLog';
import { isGateUnlocked as checkGateUnlocked, unlockGate } from './lib/storage';
import './globals.css';
import './styles.css';

import { useParams } from 'react-router-dom';

// Multi-tenant pages (lazy-loaded, only used when MULTI_TENANT=true)
const LoginPage = lazy(() =>
  import('./components/pages/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const RegisterPage = lazy(() =>
  import('./components/pages/RegisterPage').then((m) => ({ default: m.RegisterPage }))
);
const RoomListPage = lazy(() =>
  import('./components/pages/RoomListPage').then((m) => ({ default: m.RoomListPage }))
);
const MTDashboardPage = lazy(() =>
  import('./components/pages/MTDashboardPage').then((m) => ({ default: m.MTDashboardPage }))
);
const MTSettingsPage = lazy(() =>
  import('./components/pages/MTSettingsPage').then((m) => ({ default: m.MTSettingsPage }))
);
const AdminDashboard = lazy(() =>
  import('./components/pages/admin/AdminDashboard').then((m) => ({ default: m.AdminDashboard }))
);
const UserManagement = lazy(() =>
  import('./components/pages/admin/UserManagement').then((m) => ({ default: m.UserManagement }))
);
const CredentialVault = lazy(() =>
  import('./components/pages/admin/CredentialVault').then((m) => ({ default: m.CredentialVault }))
);
const TraderActivity = lazy(() =>
  import('./components/pages/admin/TraderActivity').then((m) => ({ default: m.TraderActivity }))
);
const RequireAuth = lazy(() =>
  import('./components/pages/admin/RequireAuth').then((m) => ({ default: m.RequireAuth }))
);
const RebateCalculatorView = lazy(() => import('./components/pages/RebateCalculatorView'));

// ST views wrapped for MT navigation (empty defaults, data wiring TBD)
const MTExplorePage = lazy(() =>
  import('./components/pages/MTExplorePage').then((m) => ({ default: m.MTExplorePage }))
);
const MTPositionsPage = lazy(() =>
  import('./components/pages/MTPositionsPage').then((m) => ({ default: m.MTPositionsPage }))
);
const MTOrdersPage = lazy(() =>
  import('./components/pages/MTOrdersPage').then((m) => ({ default: m.MTOrdersPage }))
);
const MTPositionTrackerPage = lazy(() =>
  import('./components/pages/MTPositionTrackerPage').then((m) => ({
    default: m.MTPositionTrackerPage,
  }))
);
const MTDiagnosticsPage = lazy(() =>
  import('./components/pages/MTDiagnosticsPage').then((m) => ({ default: m.MTDiagnosticsPage }))
);
const TradingRoomView = lazy(() =>
  import('./components/pages/TradingRoomView').then((m) => ({ default: m.TradingRoomView }))
);
const MTLayout = lazy(() =>
  import('./components/layout/MTLayout').then((m) => ({ default: m.MTLayout }))
);

// Multi-tenant context (lazy-loaded)
const AuthProviderModule = import('./contexts/AuthContext');
const LazyAuthProvider = lazy(() => AuthProviderModule.then((m) => ({ default: m.AuthProvider })));
const RoomProviderModule = import('./contexts/RoomContext');
const LazyRoomProvider = lazy(() => RoomProviderModule.then((m) => ({ default: m.RoomProvider })));

/**
 * Wrapper that extracts roomId from URL params and wraps TradingRoomView in RoomProvider.
 */
function RoomRouteWrapper() {
  const { roomId } = useParams<{ roomId: string }>();
  if (!roomId) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">No room specified</p>
      </div>
    );
  }
  return (
    <Suspense fallback={<Loading />}>
      <LazyRoomProvider roomId={roomId}>
        <TradingRoomView />
      </LazyRoomProvider>
    </Suspense>
  );
}

// ── Global error handlers (fire before React mounts) ───────────────
window.onerror = (_msg, source, lineno, colno, error) => {
  sendClientLog('error', error?.message || String(_msg), {
    source: `${source || 'unknown'}:${lineno}:${colno}`,
    stack: error?.stack,
  });
};

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message = reason instanceof Error ? reason.message : String(reason);
  sendClientLog('error', message, {
    source: 'unhandledrejection',
    stack: reason instanceof Error ? reason.stack : undefined,
  });
});

// Router basename for subdirectory deployment (e.g., "/decoy_folder/dashboard")
const routerBasename = import.meta.env.VITE_ROUTER_BASE || '';
const isMultiTenant = import.meta.env.VITE_MULTI_TENANT === 'true';
const MT_GATE_HASH = import.meta.env.VITE_DASHBOARD_PASSWORD_HASH || '';

// Lazy-load GateView for MT mode
const GateViewLazy = lazy(() =>
  import('./components/pages/GateView').then((m) => ({ default: m.GateView }))
);

function Loading() {
  return (
    <div className="bg-background flex min-h-screen items-center justify-center">
      <div className="text-muted-foreground text-sm">Loading...</div>
    </div>
  );
}

/**
 * Multi-tenant routes: gate → login → MTLayout (sidebar + content)
 * Gate is deployment-level (shared password), login is user-level (JWT).
 * MTLayout handles auth guard — redirects to /login if not authenticated.
 */
function MultiTenantApp() {
  const [gateUnlocked, setGateUnlocked] = React.useState(() =>
    MT_GATE_HASH ? checkGateUnlocked(MT_GATE_HASH) : true
  );

  const handleGateUnlock = React.useCallback((verifiedHash: string, remember: boolean) => {
    unlockGate(verifiedHash, remember);
    setGateUnlocked(true);
  }, []);

  // Show gate password screen first (if configured)
  if (!gateUnlocked) {
    return (
      <Suspense fallback={<Loading />}>
        <GateViewLazy onUnlock={handleGateUnlock} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<Loading />}>
      <LazyAuthProvider>
        <Routes>
          {/* Public auth routes (no sidebar) */}
          <Route
            path="/login"
            element={<LoginPage />}
          />
          <Route
            path="/register"
            element={<RegisterPage />}
          />

          {/* Authenticated routes — wrapped in MTLayout (sidebar + header) */}
          <Route element={<MTLayout />}>
            {/* Dashboard — default landing page */}
            <Route
              path="/dashboard"
              element={<MTDashboardPage />}
            />

            {/* Trading views (MT wrappers with empty defaults) */}
            <Route
              path="/explore"
              element={<MTExplorePage />}
            />
            <Route
              path="/positions"
              element={<MTPositionsPage />}
            />
            <Route
              path="/orders"
              element={<MTOrdersPage />}
            />
            <Route
              path="/position-tracker"
              element={<MTPositionTrackerPage />}
            />
            <Route
              path="/diagnostics"
              element={<MTDiagnosticsPage />}
            />

            {/* Rooms */}
            <Route
              path="/rooms"
              element={<RoomListPage />}
            />

            {/* Settings */}
            <Route
              path="/settings"
              element={<MTSettingsPage />}
            />

            {/* Admin routes (role-gated) */}
            <Route
              path="/admin"
              element={
                <RequireAuth role="admin">
                  <AdminDashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/users"
              element={
                <RequireAuth role="admin">
                  <UserManagement />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/credentials"
              element={
                <RequireAuth role="admin">
                  <CredentialVault />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/activity"
              element={
                <RequireAuth role="admin">
                  <TraderActivity />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/rebates"
              element={
                <RequireAuth role="admin">
                  <RebateCalculatorView />
                </RequireAuth>
              }
            />
          </Route>

          {/* Room view — full-screen, outside MTLayout (has its own chrome) */}
          <Route
            path="/room/:roomId"
            element={<RoomRouteWrapper />}
          />

          {/* Default: redirect to dashboard (MTLayout will bounce to /login if unauthed) */}
          <Route
            path="*"
            element={
              <Navigate
                to="/dashboard"
                replace
              />
            }
          />
        </Routes>
      </LazyAuthProvider>
    </Suspense>
  );
}

/**
 * Single-user routes: existing App at all paths
 */
function SingleUserApp() {
  return (
    <Routes>
      {/* Login route */}
      <Route
        path="/"
        element={<App />}
      />

      {/* Authenticated app routes */}
      <Route
        path="/app"
        element={<App />}
      />

      {/* Explore hierarchy routes */}
      <Route
        path="/app/explore"
        element={<App />}
      />
      <Route
        path="/app/explore/:category"
        element={<App />}
      />
      <Route
        path="/app/explore/:category/:series"
        element={<App />}
      />
      <Route
        path="/app/explore/:category/:series/:event"
        element={<App />}
      />

      {/* Direct market route */}
      <Route
        path="/app/market/:ticker"
        element={<App />}
      />

      {/* Other views */}
      <Route
        path="/app/:view"
        element={<App />}
      />

      {/* Catch-all redirect */}
      <Route
        path="*"
        element={
          <Navigate
            to="/"
            replace
          />
        }
      />
    </Routes>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <ActivityProvider>
        <HotkeyProvider>
          <ErrorBoundary>
            <BrowserRouter basename={routerBasename}>
              {isMultiTenant ? <MultiTenantApp /> : <SingleUserApp />}
            </BrowserRouter>
          </ErrorBoundary>
        </HotkeyProvider>
      </ActivityProvider>
    </ThemeProvider>
  </React.StrictMode>
);
